import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { loadPlanCaseload } from "../../../application/planCaseload";
import type { Patient, CouplePatient } from "../../../../patient";

import Label from "../../../../../shared/ui/form/Label";
import Input from "../../../../../shared/ui/form/input/InputField";
import FilterSelect from "../../../../../shared/ui/form/FilterSelect";
import { User, Users } from "lucide-react";
import { useCourseLabels, useSessionPreferences } from "../../../../center";

interface Props {
  therapistID: string;
  patientId: string;
  couplePatientId?: string;
  type: 'individual' | 'couple';
  onPatientChange: (id: string) => void;
  onCoupleChange: (id: string | undefined) => void;
  onTypeChange: (type: 'individual' | 'couple') => void;
  title: string;
  onTitleChange: (v: string) => void;
}

export default function TherapeuticPlanAddBasicForm({
  therapistID,
  patientId,
  couplePatientId,
  type,
  onPatientChange,
  onCoupleChange,
  onTypeChange,
  title,
  onTitleChange,
}: Props) {
  const { t } = useTranslation();
  // `name` is the person alone, for the title; `label` may carry their course.
  const [patients, setPatients] = useState<{ value: string; label: string; name?: string }[]>([]);
  // A training centre's student is named with the course they are on.
  const { labelOf } = useCourseLabels();
  const [couples, setCouples] = useState<{ value: string; label: string }[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!therapistID) return;
    setLoading(true);

    loadPlanCaseload(therapistID)
      .then(({ patients: pList, couples: cList }) => {
        const pData = pList || [];
        setPatients(pData.map((p: Patient) => ({
          value: p.id,
          label: labelOf(p),
          name: `${p.first_name} ${p.last_name}`,
        })));

        const cData = cList || [];
        setCouples(cData.map((c: CouplePatient) => ({
          value: c.id,
          label: c.display_name,
        })));
      })
      .catch((err: unknown) => {
        console.error("Error fetching data:", err);
      })
      .finally(() => setLoading(false));
  }, [therapistID, labelOf]);

  // A plan is for one person or one couple, offered only as the centre runs
  // them. With couples off there is nothing to choose between.
  const { modes } = useSessionPreferences();
  const planTypes: ('individual' | 'couple')[] = modes.couple
    ? modes.individual ? ['individual', 'couple'] : ['couple']
    : ['individual'];
  useEffect(() => {
    if (!planTypes.includes(type)) onTypeChange(planTypes[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, modes.individual, modes.couple]);

  const activeOptions = useMemo((): { value: string; label: string; name?: string }[] => {
    return type === 'individual' ? patients : couples;
  }, [type, patients, couples]);

  return (
    <div className="glass-effect glass-border border rounded-3xl p-6 shadow-theme-sm">
      <h3 className="text-xs font-bold text-content-tertiary uppercase tracking-widest px-1 mb-6">
        {t("therapeuticPlan.basicInfo")}
      </h3>

      <div className="space-y-6">
        {/* Toggle between Individual and Couple */}
        {planTypes.length > 1 && (
        <div>
          <Label className="mb-3 uppercase text-xs font-semibold text-content-tertiary tracking-wider">
            {t("therapeuticPlan.typeTitle")}
          </Label>
          <div className="flex bg-gray-100 p-1 rounded-xl w-full">
            <button
              type="button"
              onClick={() => onTypeChange('individual')}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${type === 'individual' ? 'bg-white text-accent-text shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
            >
              <User className="w-4 h-4" />
              <span>{t("session.typeOptions.individual")}</span>
            </button>
            <button
              type="button"
              onClick={() => onTypeChange('couple')}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${type === 'couple' ? 'bg-white text-accent-text shadow-sm' : 'text-gray-500 hover:text-gray-900'}`}
            >
              <Users className="w-4 h-4" />
              <span>{t("session.typeOptions.couple")}</span>
            </button>
          </div>
        </div>
        )}

        <div className="grid grid-cols-1 gap-6">
          {/* Patient/Couple Selection */}
          <div>
            <Label className="mb-2 uppercase text-xs font-semibold text-content-tertiary tracking-wider font-montserrat">
              {t("therapeuticPlan.filterPatient")}
            </Label>
            <FilterSelect
              options={activeOptions}
              value={type === 'individual' ? patientId : (couplePatientId || "")}
              placeholder={loading ? t("common.loading") : (type === 'individual' ? t("therapeuticPlan.selectPatient") : t("patient.selectPatient"))}
              searchPlaceholder={t("patient.searchPlaceholder")}
              onChange={(value) => {
                const val = value as string;
                if (type === 'individual') {
                  onPatientChange(val);
                } else {
                  onCoupleChange(val);
                }

                const selected = activeOptions.find(o => o.value === val);
                if (selected && !title) {
                  onTitleChange(`${selected.name ?? selected.label} - ${t("therapeuticPlan.pageTitle")}`); // Simplified title
                }
              }}
              className="w-full rounded-xl bg-gray-50/50 border-gray-200/50 focus:bg-white transition-all py-1"
            />
          </div>

          {/* Title */}
          <div>
            <Label className="mb-2 uppercase text-xs font-semibold text-content-tertiary tracking-wider font-montserrat">
              {t("therapeuticPlan.title")}
            </Label>
            <Input
              value={title}
              required
              className="rounded-xl bg-gray-50/50 border-gray-200/50 focus:bg-white transition-all py-3"
              onChange={(e) => onTitleChange(e.target.value)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
